'use client';

import { useMemo, useState, useEffect } from 'react';
import Link from 'next/link';
import Image from '@src/components/atoms/safe-image';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import styles from './card-property.module.css';

// Assets
import IDot from '@public/png/icon-dot.png';
import IArrowRound from '@public/svg/i-arrow-round.jsx';

// Components
import Button from '@src/components/atoms/button';

// Helper
import formatNominal from '@src/helper/formatNominal';
import formatTimeAgo from '@src/helper/formatTime';

const PrevArrow = ({ onClick, show }) => {
  if (!show) return null;
  return (
    <button
      onClick={onClick}
      className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-6 h-6 flex items-center justify-center transition-opacity duration-200"
      aria-label="Previous"
    >
      <IArrowRound />
    </button>
  );
};

const NextArrow = ({ onClick, show }) => {
  if (!show) return null;
  return (
    <button
      onClick={onClick}
      className="absolute right-2 rotate-180 top-1/2 -translate-y-1/2 z-10 w-6 h-6 flex items-center justify-center transition-opacity duration-200"
      aria-label="Next"
    >
      <IArrowRound />
    </button>
  );
};

const CardPropertySlider = ({
  images,
  price,
  updated_at,
  area,
  city,
  title,
  property_category,
  lastItemRef,
  user,
  company,
  bedroom,
  bathroom,
  building_area,
  surface_area,
  unit_price,
  property_type,
  href,
  handleTanyaMarketing,
  special_tags,
  showButtonHubungkan = true,
  showUserName = true,
  showCompany = true,
  showUserInfo = true,
  priority = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  const KT = typeof +bedroom === 'number' && !!+bedroom ? +bedroom : null;
  const KM = typeof +bathroom === 'number' && !!+bathroom ? +bathroom : null;
  const LT =
    typeof +surface_area === 'number' && +surface_area ? +surface_area : null;
  const LB =
    typeof +building_area === 'number' && +building_area
      ? +building_area
      : null;
  const features = [
    KT && `${KT} KT`,
    KM && `${KM} KM`,
    LT && `LT ${LT}`,
    LB && `LB ${LB}`,
  ].filter(Boolean);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const formattedPropertyCategory = useMemo(() => {
    if (!special_tags?.length) return property_category;

    return `${special_tags[0]?.name}`;
  }, [special_tags, property_category]);

  const handleUnitPrice = useMemo(() => {
    if (!unit_price) return '';

    if (unit_price.includes('/')) {
      return unit_price.replace('Unit', '').trim();
    }

    if (unit_price.includes('Per')) {
      return unit_price.replace('Per', '/').trim();
    }

    return unit_price.trim();
  }, [unit_price]);

  const showArrows = isHovered && windowWidth >= 991;

  const sliderSettings = {
    dots: true,
    infinite: images?.length > 1,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: showArrows,
    prevArrow: <PrevArrow show={showArrows} />,
    nextArrow: <NextArrow show={showArrows} />,
    swipe: true,
    touchMove: true,
    dotsClass: 'slick-dots !bottom-2',
  };

  return (
    <Link href={href} ref={lastItemRef}>
      <div className="p-1 rounded-[16px] border-[0.5px] border-solid border-[#DEDFDE] bg-white shadow-[0_0_2px_0_rgba(4,7,4,0.08)]">
        <div
          className="relative w-full aspect-square rounded-[12px] border-[0.5px] border-solid border-[#DEDFDE] mb-[12px] overflow-hidden"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {images?.length > 0 ? (
            <div className={styles.sliderContainer}>
              <Slider {...sliderSettings}>
                {images.map((image, index) => (
                  <div key={index} className="relative w-full aspect-square">
                    <Image
                      src={image}
                      alt={`${title} - ${index + 1}`}
                      width={1000}
                      height={1000}
                      sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, (max-width: 1536px) 33vw, 25vw"
                      priority={priority && index === 0}
                      className="w-full h-full object-cover rounded-[8px]"
                    />
                  </div>
                ))}
              </Slider>
            </div>
          ) : null}

          <span className="absolute z-20 top-2 left-2 text-[14px]/[19.6px] font-satoshi-bold text-[#fafafa] px-2 py-0.5 rounded-[8px] bg-[#040704]">
            {formattedPropertyCategory}
          </span>
        </div>

        <div className="px-2 pb-2 flex flex-col gap-[2px]">
          <div className="flex items-center justify-between">
            <p className="text-[21px]/[25.2px] tracking-[-0.42px] font-satoshi-bold">
              Rp{formatNominal(price || 0)}
              {!!(
                property_type === 'Disewakan' || property_category === 'Tanah'
              ) && (
                <span className="text-[14px]/[19.6px] font-satoshi text-[#686A68]">
                  {handleUnitPrice}
                </span>
              )}
            </p>
            <p className="text-[11px]/[15.4px] tracking-[0.11px] font-satoshi text-[#686A68]">
              Diperbarui {formatTimeAgo(updated_at)?.replace('yang', '')}
            </p>
          </div>
          <p className="text-[14px]/[19.6px] tracking-[-0.17px] font-satoshi pb-[1px]">
            {area ?? ''}, {city ?? ''}
          </p>
          {!!features?.length && (
            <div className="flex items-center py-[2px]">
              {features.map((f, i) => {
                return (
                  <div key={`${i}-features-${f}`} className="flex items-center">
                    <p className="text-[14px]/[19.6px] font-satoshi text-[#808285]">
                      {f}
                      {f.includes('LT') ||
                        (f.includes('LB') && (
                          <span>
                            m<sup>2</sup>
                          </span>
                        ))}
                    </p>
                    {i < features.length - 1 && (
                      <Image
                        width="auto"
                        height="auto"
                        src={IDot}
                        alt="dot"
                        className="w-2 h-2 object-contain mx-2"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-[14px]/[19.6px] font-satoshi text-[#808285] truncate">
            {title}
          </p>

          {showUserInfo && (
            <div className="flex items-end justify-between mt-[6px]">
              <div>
                {showUserName && (
                  <p className="text-[14px]/[19.6px] font-satoshi truncate">
                    {user?.full_name ?? ''}
                  </p>
                )}
                {showCompany && (
                  <p className="text-[14px]/[19.6px] font-satoshi-bold text-[#808285]">
                    CENTURY 21{' '}
                    <span className="font-satoshi-medium text-[11px]/[15.4px] tracking-[0.11px]">
                      {company?.name ?? ''}
                    </span>
                  </p>
                )}
              </div>
              {showButtonHubungkan && (
                <Button
                  type="button"
                  className="!py-0 h-[32px]"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleTanyaMarketing();
                  }}
                >
                  Hubungi
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

export default CardPropertySlider;
